import { Component } from '@theme/component';

/**
 * @typedef {object} FooterRefs
 * @property {HTMLDetailsElement[]} [columns] - The menu columns, each a disclosure.
 */

/**
 * The width below which a menu column is a disclosure rather than a heading
 * standing over its links. Keep in sync with the section's mobile breakpoint.
 *
 * @type {MediaQueryList}
 */
const collapsible = matchMedia('(max-width: 749px)');

/**
 * The footer's menu columns.
 *
 * The columns are rendered open, so every link is in the document and on screen
 * before this runs and for anyone without script. All this does is close them
 * on a phone and take the summary out of the tab order above that width, where
 * CSS has already stopped it taking clicks — a column that can't be closed
 * shouldn't be a stop on the way to the links either.
 *
 * @extends {Component<FooterRefs>}
 */
class FooterMenus extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#sync();

    /**
     * The columns arrive open, so the first sync on a phone is four columns
     * closing at once — which must not be watched. Two frames, because a flag
     * set in the first one can still be picked up by the same style pass that
     * closed them, and the transition would play after all.
     */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.setAttribute('ready', ''));
    });

    collapsible.addEventListener('change', this.#sync);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    collapsible.removeEventListener('change', this.#sync);
  }

  updatedCallback() {
    super.updatedCallback();

    this.#sync();
  }

  /**
   * Puts every column in the state its width calls for: closed and focusable on
   * a phone, open and skipped by the keyboard everywhere else.
   */
  #sync = () => {
    const collapsed = collapsible.matches;

    for (const column of this.refs.columns ?? []) {
      column.open = !collapsed;

      const summary = column.querySelector('summary');

      if (summary) summary.tabIndex = collapsed ? 0 : -1;
    }
  };
}

if (!customElements.get('footer-menus')) {
  customElements.define('footer-menus', FooterMenus);
}
